/**
 * Enterprise User Preferences Service
 * 
 * Production-ready service for managing all user preferences in Supabase,
 * completely replacing localStorage with persistent, synced storage.
 * Implements debouncing, caching, and optimistic updates for performance.
 * 
 * @author Senior Engineering Team
 * @version 1.0.0
 * @since 2024-06-24
 */

import { supabase } from '@/utils/supabase';
import {
  ServiceResult,
  UserPreferences,
  UIPreferences,
  WorkflowPreferences,
  NotificationPreferences,
  IntegrationPreferences,
  FilterPreferences,
  FeatureSettings,
  UpdatePreferencesRequest,
  BaseServiceOptions,
  CacheEntry
} from '@/types/services';
import { 
  ServiceError, 
  ErrorFactory, 
  ErrorCode 
} from '@/types/errors';
import { logger, createOperationContext } from '@/utils/serviceLogger';
import { 
  validateData,
  updatePreferencesRequestSchema
} from '@/utils/serviceValidation';

/**
 * Service configuration
 */
const SERVICE_CONFIG = {
  name: 'UserPreferencesService',
  defaultTimeout: 5000, // 5 seconds
  debounceDelay: 300, // 300ms debounce for updates
  cacheEnabled: true,
  cacheTtl: 600, // 10 minutes
  schemaVersion: 1,
  maxRetries: 3
} as const;

/**
 * Default preferences factory based on user role
 */
class PreferencesFactory {
  /**
   * Get default UI preferences
   */
  static getDefaultUIPreferences(role?: string): UIPreferences {
    return {
      theme: 'system',
      sidebarCollapsed: false,
      density: 'comfortable',
      language: 'en',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
      dateFormat: 'MM/DD/YYYY',
      currencyFormat: 'USD',
      defaultView: role === 'admin' ? 'grid' : 'list',
      animationsEnabled: true,
      loanCenterDirection: 'table',
      homeWidgets: {
        removed: []
      }
    };
  }

  /**
   * Get default workflow preferences
   */
  static getDefaultWorkflowPreferences(role?: string): WorkflowPreferences {
    return {
      defaultPreApprovalTemplateId: undefined, // User's personal default pre-approval template
      autoSaveInterval: 5, // 5 minutes
      requireApprovalForSending: role === 'processor',
      notificationChannels: ['email', 'in_app'],
      reminderSettings: {
        followUpDays: 3,
        expirationWarningDays: 1,
        enabled: true
      },
      pipelineStages: [
        'Lead',
        'Pre-Approval',
        'Application',
        'Processing',
        'Underwriting',
        'Approved',
        'Closing',
        'Funded'
      ],
      customFields: []
    };
  }

  /**
   * Get default notification preferences
   */
  static getDefaultNotificationPreferences(): NotificationPreferences {
    return {
      emailNotifications: {
        newApplication: true,
        systemUpdates: false,
        marketingEmails: false
      },
      smsNotifications: {
        urgentAlerts: true,
        dailyDigest: false,
        weeklyReport: false
      },
      inAppNotifications: {
        realTimeUpdates: true,
        soundEnabled: true,
        desktopNotifications: false
      },
      digestFrequency: 'daily',
      quietHours: {
        enabled: false,
        startTime: '21:00',
        endTime: '08:00',
        timezone: 'America/New_York'
      }
    };
  }

  /**
   * Get default integration preferences
   */
  static getDefaultIntegrationPreferences(): IntegrationPreferences {
    return {};
  }

  /**
   * Get default filter preferences
   */
  static getDefaultFilterPreferences(): FilterPreferences {
    return {
      preApprovals: {
        status: undefined,
        dateRange: 'month',
        sortBy: 'createdAt',
        sortOrder: 'desc',
        pageSize: 25,
        showArchived: false
      },
      contacts: {
        type: 'all',
        sortBy: 'lastName',
        sortOrder: 'asc'
      }
    };
  }

  /**
   * Get default feature settings
   */
  static getDefaultFeatureSettings(): FeatureSettings {
    return {
      enabledFeatures: [],
      betaFeatures: [],
      featureFlags: {},
      experimentGroups: {}
    };
  }

  /**
   * Create complete default preferences for a user
   */
  static createDefaultPreferences(_userId: string, organizationId: string, role?: string): Omit<UserPreferences, 'id' | 'audit'> {
    return {
      _userId,
      organizationId,
      uiPreferences: this.getDefaultUIPreferences(role),
      workflowPreferences: this.getDefaultWorkflowPreferences(role),
      notificationPreferences: this.getDefaultNotificationPreferences(),
      integrationPreferences: this.getDefaultIntegrationPreferences(),
      filterPreferences: this.getDefaultFilterPreferences(),
      featureSettings: this.getDefaultFeatureSettings(),
      schemaVersion: SERVICE_CONFIG.schemaVersion
    };
  }
}

/**
 * Debounce manager for batching preference updates
 */
class DebounceManager {
  private pendingUpdates = new Map<string, {
    updates: UpdatePreferencesRequest;
    timer: NodeJS.Timeout;
    executor: (updates: UpdatePreferencesRequest) => Promise<ServiceResult<void>>;
    waiters: Array<{
      resolve: (result: ServiceResult<void>) => void;
      reject: (error: Error) => void;
    }>;
  }>();
  private inFlightExecutions = new Map<string, Promise<void>>();

  private schedule(_userId: string, delay: number): NodeJS.Timeout {
    return setTimeout(() => {
      void this.executePending(_userId);
    }, delay);
  }

  private async executePending(_userId: string): Promise<void> {
    const pending = this.pendingUpdates.get(_userId);
    if (!pending) {
      return;
    }

    this.pendingUpdates.delete(_userId);

    const priorExecution = this.inFlightExecutions.get(_userId)
      ?? Promise.resolve();
    const execution = priorExecution.then(async () => {
      try {
        const result = await pending.executor(pending.updates);
        pending.waiters.forEach(({ resolve }) => resolve(result));
      } catch (error) {
        pending.waiters.forEach(({ reject }) => reject(error as Error));
      }
    });
    this.inFlightExecutions.set(_userId, execution);

    await execution;
    if (this.inFlightExecutions.get(_userId) === execution) {
      this.inFlightExecutions.delete(_userId);
    }
  }

  /**
   * Add update to debounce queue
   */
  debounce(
    _userId: string,
    updates: UpdatePreferencesRequest,
    executor: (updates: UpdatePreferencesRequest) => Promise<ServiceResult<void>>,
    delay: number = SERVICE_CONFIG.debounceDelay
  ): Promise<ServiceResult<void>> {
    return new Promise((resolve, reject) => {
      const existing = this.pendingUpdates.get(_userId);

      if (existing) {
        clearTimeout(existing.timer);
        this.pendingUpdates.set(_userId, {
          updates: this.mergeUpdates(existing.updates, updates),
          timer: this.schedule(_userId, delay),
          executor,
          waiters: [...existing.waiters, { resolve, reject }]
        });
        return;
      }

      this.pendingUpdates.set(_userId, {
        updates,
        timer: this.schedule(_userId, delay),
        executor,
        waiters: [{ resolve, reject }]
      });
    });
  }

  /**
   * Merge two update requests
   */
  private mergeUpdates(
    existing: UpdatePreferencesRequest,
    incoming: UpdatePreferencesRequest
  ): UpdatePreferencesRequest {
    return {
      uiPreferences: incoming.uiPreferences 
        ? { ...existing.uiPreferences, ...incoming.uiPreferences }
        : existing.uiPreferences,
      workflowPreferences: incoming.workflowPreferences
        ? { ...existing.workflowPreferences, ...incoming.workflowPreferences }
        : existing.workflowPreferences,
      notificationPreferences: incoming.notificationPreferences
        ? { ...existing.notificationPreferences, ...incoming.notificationPreferences }
        : existing.notificationPreferences,
      integrationPreferences: incoming.integrationPreferences
        ? { ...existing.integrationPreferences, ...incoming.integrationPreferences }
        : existing.integrationPreferences,
      filterPreferences: incoming.filterPreferences
        ? { ...existing.filterPreferences, ...incoming.filterPreferences }
        : existing.filterPreferences,
      featureSettings: incoming.featureSettings
        ? { ...existing.featureSettings, ...incoming.featureSettings }
        : existing.featureSettings
    };
  }

  /**
   * Flush all pending updates immediately
   */
  async flushAll(): Promise<void> {
    const pending = Array.from(this.pendingUpdates.entries());
    const activeExecutions = Array.from(this.inFlightExecutions.values());

    for (const [_userId, { timer }] of pending) {
      clearTimeout(timer);
    }

    await Promise.all([
      ...activeExecutions,
      ...pending.map(([_userId]) => this.executePending(_userId))
    ]);
  }

  /**
   * Get pending update count for monitoring
   */
  getPendingCount(): number {
    return this.pendingUpdates.size;
  }

  hasUpdates(_userId: string): boolean {
    return this.pendingUpdates.has(_userId) || this.inFlightExecutions.has(_userId);
  }
}

/**
 * In-memory cache for user preferences
 * NO localStorage usage - memory only
 */
class PreferencesCache {
  private cache = new Map<string, CacheEntry<UserPreferences>>();
  
  set(_userId: string, preferences: UserPreferences): void {
    if (!SERVICE_CONFIG.cacheEnabled) {
return;
}

    const expiresAt = Date.now() + (SERVICE_CONFIG.cacheTtl * 1000);
    this.cache.set(_userId, {
      data: preferences,
      cachedAt: Date.now(),
      expiresAt,
      tags: ['preferences', _userId],
      hits: 0,
      lastAccessed: Date.now()
    });

    // Log cache size for monitoring (potential memory leak detection)
    if (this.cache.size > 10000) {
      logger.warn(
        'Preferences cache size exceeds 10,000 entries',
        'cache_monitor',
        SERVICE_CONFIG.name,
        'cache.set',
        { cacheSize: this.cache.size }
      );
    }
  }

  get(_userId: string): UserPreferences | null {
    if (!SERVICE_CONFIG.cacheEnabled) {
return null;
}

    const entry = this.cache.get(_userId);
    if (!entry) {
return null;
}

    if (Date.now() > entry.expiresAt) {
      this.cache.delete(_userId);
      return null;
    }

    entry.hits++;
    entry.lastAccessed = Date.now();
    return entry.data;
  }

  invalidate(_userId: string): void {
    this.cache.delete(_userId);
  }

  clear(): void {
    this.cache.clear();
  }

  getStats() {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.entries()).map(([_userId, entry]) => ({
        _userId,
        hits: entry.hits,
        age: Date.now() - entry.cachedAt,
        expiresIn: entry.expiresAt - Date.now()
      }))
    };
  }
}

/**
 * Enterprise User Preferences Service Implementation
 * Completely replaces localStorage with Supabase persistence
 */
export class UserPreferencesService {
  private static instance: UserPreferencesService;
  private cache = new PreferencesCache();
  private cacheRevision = 0;
  private debounceManager = new DebounceManager();
  private preferencePreparationTail: Promise<void> = Promise.resolve();
  
  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get singleton instance
   */
  static getInstance(): UserPreferencesService {
    if (!UserPreferencesService.instance) {
      UserPreferencesService.instance = new UserPreferencesService();
    }
    return UserPreferencesService.instance;
  }

  private enqueuePreferencePreparation<T>(
    prepare: () => Promise<T>
  ): Promise<T> {
    const run = this.preferencePreparationTail.then(prepare);
    this.preferencePreparationTail = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  }

  /**
   * Get user preferences with auto-creation if missing
   * 
   * @example
   * ```typescript
   * const result = await userPreferencesService.getPreferences();
   * if (result.success) {
   *   applyTheme(result.data.uiPreferences.theme);
   *   setSidebarState(result.data.uiPreferences.sidebarCollapsed);
   * }
   * ```
   */
  async getPreferences(options: BaseServiceOptions = {}): Promise<ServiceResult<UserPreferences>> {
    const context = createOperationContext(SERVICE_CONFIG.name, 'getPreferences', options);
    const timer = logger.startOperation(context.operationId);
    const readRevision = this.cacheRevision;

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw ErrorFactory.permission(
          ErrorCode.USER_NOT_AUTHENTICATED,
          'User must be authenticated to access preferences',
          context.correlationId
        );
      }

      logger.info('Getting user preferences', context.correlationId, SERVICE_CONFIG.name, 'getPreferences', {
        _userId: user.id
      });

      // Check cache first
      const cached = this.cache.get(user.id);
      if (cached) {
        timer.recordCacheHit();
        logger.logCacheMetrics(context.correlationId, SERVICE_CONFIG.name, 'getPreferences', user.id, true);
        
        const _metrics = logger.endOperation(context.operationId);
        return {
          success: true,
          data: cached,
          correlationId: context.correlationId,
          _metrics
        };
      }
      timer.recordCacheMiss();
      const writePendingAtRead = this.debounceManager.hasUpdates(user.id);

      // Get user's profile for role and organization
      timer.recordDatabaseQuery();
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('organization_id, roles')
        .eq('id', user.id)
        .single();

      if (profileError || !profile?.organization_id) {
        throw ErrorFactory.permission(
          ErrorCode.ORGANIZATION_MISMATCH,
          'User must belong to an organization',
          context.correlationId,
          { _userId: user.id, profileError }
        );
      }

      // Try to fetch existing preferences
      timer.recordDatabaseQuery();
      const { data: existingPrefs, error: fetchError } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 = not found
        throw ErrorFactory.database(
          ErrorCode.DATABASE_CONNECTION_FAILED,
          `Failed to fetch preferences: ${fetchError.message}`,
          true,
          context.correlationId,
          { supabaseError: fetchError }
        );
      }

      let preferences: UserPreferences;

      if (existingPrefs) {
        // Map existing preferences
        preferences = this.mapSupabaseToPreferences(existingPrefs);
      } else {
        // Create default preferences
        logger.info('Creating default preferences for new user', context.correlationId, SERVICE_CONFIG.name, 'getPreferences', {
          _userId: user.id,
          role: profile.roles?.[0]
        });

        const defaultPrefs = PreferencesFactory.createDefaultPreferences(
          user.id,
          profile.organization_id,
          profile.roles?.[0]
        );

        // Insert default preferences
        timer.recordDatabaseQuery();
        const { data: newPrefs, error: createError } = await supabase
          .from('user_preferences')
          .insert([{
            user_id: user.id,
            organization_id: profile.organization_id,
            ui_preferences: defaultPrefs.uiPreferences,
            workflow_preferences: defaultPrefs.workflowPreferences,
            notification_preferences: defaultPrefs.notificationPreferences,
            integration_preferences: defaultPrefs.integrationPreferences,
            filter_preferences: defaultPrefs.filterPreferences,
            feature_settings: defaultPrefs.featureSettings,
            schema_version: defaultPrefs.schemaVersion
          }])
          .select()
          .single();

        if (createError) {
          throw ErrorFactory.database(
            ErrorCode.DATABASE_CONNECTION_FAILED,
            `Failed to create preferences: ${createError.message}`,
            false,
            context.correlationId,
            { supabaseError: createError }
          );
        }

        preferences = this.mapSupabaseToPreferences(newPrefs);
        
        // Track telemetry for new preference creation
        logger.info('Default preferences created', context.correlationId, SERVICE_CONFIG.name, 'getPreferences', {
          _userId: user.id,
          role: profile.roles?.[0],
          schemaVersion: defaultPrefs.schemaVersion
        });
      }

      // An older read must not replace a newer optimistic preference or refill
      // an invalidated cache. Reads overlapping a save may contain its old value.
      if (readRevision === this.cacheRevision && !writePendingAtRead
        && !this.debounceManager.hasUpdates(user.id)) {
        this.cache.set(user.id, preferences);
      } else {
        preferences = this.cache.get(user.id) ?? preferences;
      }

      const _metrics = logger.endOperation(context.operationId);
      logger.logOperation(
        'Preferences retrieved successfully',
        context.correlationId,
        SERVICE_CONFIG.name,
        'getPreferences',
        _metrics!,
        { _userId: user.id, fromCache: false }
      );

      return {
        success: true,
        data: preferences,
        correlationId: context.correlationId,
        _metrics
      };

    } catch (error) {
      const _metrics = logger.endOperation(context.operationId);
      
      if (error instanceof ServiceError) {
        logger.error('Failed to get preferences', error, context.correlationId, SERVICE_CONFIG.name, 'getPreferences');
        return {
          success: false,
          error,
          correlationId: context.correlationId,
          _metrics
        };
      }

      const serviceError = ErrorFactory.database(
        ErrorCode.INTERNAL_SERVER_ERROR,
        'Unexpected error getting preferences',
        false,
        context.correlationId,
        { originalError: error }
      );

      logger.error('Unexpected error in getPreferences', serviceError, context.correlationId, SERVICE_CONFIG.name, 'getPreferences');
      return {
        success: false,
        error: serviceError,
        correlationId: context.correlationId,
        _metrics
      };
    }
  }

  /**
   * Update UI preferences with debouncing
   * 
   * @example
   * ```typescript
   * // Toggle sidebar
   * await userPreferencesService.updateUIPreferences({
   *   sidebarCollapsed: true
   * });
   * 
   * // Change theme
   * await userPreferencesService.updateUIPreferences({
   *   theme: 'dark'
   * });
   * ```
   */
  async updateUIPreferences(
    preferences: Partial<UIPreferences>,
    options: BaseServiceOptions = {}
  ): Promise<ServiceResult<void>> {
    return this.batchUpdate({ uiPreferences: preferences }, options);
  }

  /**
   * Update workflow preferences with debouncing
   */
  async updateWorkflowPreferences(
    preferences: Partial<WorkflowPreferences>,
    options: BaseServiceOptions = {}
  ): Promise<ServiceResult<void>> {
    return this.batchUpdate({ workflowPreferences: preferences }, options);
  }

  /**
   * Update notification preferences with debouncing
   */
  async updateNotificationPreferences(
    preferences: Partial<NotificationPreferences>,
    options: BaseServiceOptions = {}
  ): Promise<ServiceResult<void>> {
    return this.batchUpdate({ notificationPreferences: preferences }, options);
  }

  /**
   * Update filter preferences with debouncing
   */
  async updateFilterPreferences(
    preferences: Partial<FilterPreferences>,
    options: BaseServiceOptions = {}
  ): Promise<ServiceResult<void>> {
    // Skip update if preferences is empty or undefined
    if (!preferences || Object.keys(preferences).length === 0) {
      logger.info('[UserPreferencesService] No filter preferences to update, skipping...');
      return { success: true, data: undefined };
    }
    
    return this.batchUpdate({ filterPreferences: preferences }, options);
  }

  /**
   * Batch update multiple preference categories
   * Implements debouncing to prevent database spam
   */
  async batchUpdate(
    updates: UpdatePreferencesRequest,
    options: BaseServiceOptions = {}
  ): Promise<ServiceResult<void>> {
    const context = createOperationContext(SERVICE_CONFIG.name, 'batchUpdate', options);
    let _userId: string | undefined;

    try {
      const authLookup = supabase.auth.getUser();
      void authLookup.catch(() => undefined);
      const prepared = await this.enqueuePreferencePreparation(async () => {
        const { data: { user } } = await authLookup;
        if (!user) {
          throw ErrorFactory.permission(
            ErrorCode.USER_NOT_AUTHENTICATED,
            'User must be authenticated to update preferences',
            context.correlationId
          );
        }

        _userId = user.id;

        // Skip update if no actual preference updates provided
        const hasValidUpdates = Object.keys(updates).some(key => {
          const value = updates[key as keyof UpdatePreferencesRequest];
          return value !== undefined && value !== null &&
                 (typeof value !== 'object' || Object.keys(value).length > 0);
        });

        if (!hasValidUpdates) {
          logger.info('[UserPreferencesService] No valid preference updates provided, skipping...');
          return {
            writePromise: Promise.resolve({
              success: true,
              data: undefined,
              correlationId: context.correlationId
            } satisfies ServiceResult<void>)
          };
        }

        // Validate update request
        const validation = validateData(updatePreferencesRequestSchema, updates, context.correlationId);
        if (!validation.success) {
          throw ErrorFactory.validation(
            validation.error.message,
            context.correlationId,
            { validationDetails: validation.error.details, updates }
          );
        }
        const validatedUpdates = validation.data;

        logger.info('Updating preferences', context.correlationId, SERVICE_CONFIG.name, 'batchUpdate', {
          _userId: user.id,
          updateCategories: Object.keys(validatedUpdates)
        });

        // Apply optimistic update to cache
        this.cacheRevision++;
        const currentPrefs = this.cache.get(user.id);
        if (currentPrefs) {
          const optimisticPrefs = this.mergePreferences(currentPrefs, validatedUpdates);
          this.cache.set(user.id, optimisticPrefs);
        }

        return {
          writePromise: this.debounceManager.debounce(
            user.id,
            validatedUpdates,
            async (mergedUpdates) => this.executeBatchUpdate(user.id, mergedUpdates, context)
          )
        };
      });

      return prepared.writePromise;

    } catch (error) {
      if (error instanceof ServiceError) {
        logger.error('Failed to update preferences', error, context.correlationId, SERVICE_CONFIG.name, 'batchUpdate');

        // Rollback optimistic update
        if (_userId) {
          this.cacheRevision++;
          this.cache.invalidate(_userId);
        }
        
        return {
          success: false,
          error,
          correlationId: context.correlationId
        };
      }

      const serviceError = ErrorFactory.database(
        ErrorCode.INTERNAL_SERVER_ERROR,
        'Unexpected error updating preferences',
        false,
        context.correlationId,
        { originalError: error }
      );

      logger.error('Unexpected error in batchUpdate', serviceError, context.correlationId, SERVICE_CONFIG.name, 'batchUpdate');

      // Rollback optimistic update
      if (_userId) {
        this.cacheRevision++;
        this.cache.invalidate(_userId);
      }
      
      return {
        success: false,
        error: serviceError,
        correlationId: context.correlationId
      };
    }
  }

  /**
   * Reset preferences to defaults
   */
  async resetToDefaults(options: BaseServiceOptions = {}): Promise<ServiceResult<void>> {
    const context = createOperationContext(SERVICE_CONFIG.name, 'resetToDefaults', options);
    const timer = logger.startOperation(context.operationId);

    try {
      // Get current user and profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw ErrorFactory.permission(
          ErrorCode.USER_NOT_AUTHENTICATED,
          'User must be authenticated to reset preferences',
          context.correlationId
        );
      }

      timer.recordDatabaseQuery();
      const { data: profile } = await supabase
        .from('users')
        .select('organization_id, roles')
        .eq('id', user.id)
        .single();

      if (!profile?.organization_id) {
        throw ErrorFactory.permission(
          ErrorCode.ORGANIZATION_MISMATCH,
          'User must belong to an organization',
          context.correlationId
        );
      }

      logger.info('Resetting preferences to defaults', context.correlationId, SERVICE_CONFIG.name, 'resetToDefaults', {
        _userId: user.id,
        role: profile.roles?.[0]
      });

      // Create default preferences
      const defaultPrefs = PreferencesFactory.createDefaultPreferences(
        user.id,
        profile.organization_id,
        profile.roles?.[0]
      );

      // Update in database
      timer.recordDatabaseQuery();
      const { error } = await supabase
        .from('user_preferences')
        .update({
          ui_preferences: defaultPrefs.uiPreferences,
          workflow_preferences: defaultPrefs.workflowPreferences,
          notification_preferences: defaultPrefs.notificationPreferences,
          integration_preferences: defaultPrefs.integrationPreferences,
          filter_preferences: defaultPrefs.filterPreferences,
          feature_settings: defaultPrefs.featureSettings,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) {
        throw ErrorFactory.database(
          ErrorCode.DATABASE_CONNECTION_FAILED,
          `Failed to reset preferences: ${error.message}`,
          false,
          context.correlationId,
          { supabaseError: error }
        );
      }

      // Invalidate cache
      this.cacheRevision++;
      this.cache.invalidate(user.id);

      const _metrics = logger.endOperation(context.operationId);
      logger.logOperation(
        'Preferences reset to defaults',
        context.correlationId,
        SERVICE_CONFIG.name,
        'resetToDefaults',
        _metrics!,
        { _userId: user.id }
      );

      return {
        success: true,
        data: undefined,
        correlationId: context.correlationId,
        _metrics
      };

    } catch (error) {
      const _metrics = logger.endOperation(context.operationId);
      
      if (error instanceof ServiceError) {
        logger.error('Failed to reset preferences', error, context.correlationId, SERVICE_CONFIG.name, 'resetToDefaults');
        return {
          success: false,
          error,
          correlationId: context.correlationId,
          _metrics
        };
      }

      const serviceError = ErrorFactory.database(
        ErrorCode.INTERNAL_SERVER_ERROR,
        'Unexpected error resetting preferences',
        false,
        context.correlationId,
        { originalError: error }
      );

      logger.error('Unexpected error in resetToDefaults', serviceError, context.correlationId, SERVICE_CONFIG.name, 'resetToDefaults');
      return {
        success: false,
        error: serviceError,
        correlationId: context.correlationId,
        _metrics
      };
    }
  }

  /**
   * Execute the actual batch update (called by debounce manager)
   */
  private async executeBatchUpdate(
    _userId: string,
    updates: UpdatePreferencesRequest,
    context: { correlationId: string; operationId: string }
  ): Promise<ServiceResult<void>> {
    const timer = logger.startOperation(context.operationId);

    try {
      // Build update object for Supabase
      const updateData: Record<string, unknown> = {
        updated_at: new Date().toISOString()
      };

      if (updates.uiPreferences) {
        updateData.ui_preferences = updates.uiPreferences;
      }
      if (updates.workflowPreferences) {
        updateData.workflow_preferences = updates.workflowPreferences;
      }
      if (updates.notificationPreferences) {
        updateData.notification_preferences = updates.notificationPreferences;
      }
      if (updates.integrationPreferences) {
        updateData.integration_preferences = updates.integrationPreferences;
      }
      if (updates.filterPreferences) {
        updateData.filter_preferences = updates.filterPreferences;
      }
      if (updates.featureSettings) {
        updateData.feature_settings = updates.featureSettings;
      }

      // Use Supabase's JSONB merge functionality
      timer.recordDatabaseQuery();
      const { error } = await supabase.rpc('update_user_preferences', {
        p_user_id: _userId,
        p_updates: updateData
      });

      if (error) {
        throw ErrorFactory.database(
          ErrorCode.DATABASE_CONNECTION_FAILED,
          `Failed to update preferences: ${error.message}`,
          true,
          context.correlationId,
          { supabaseError: error, updates }
        );
      }

      const _metrics = logger.endOperation(context.operationId);
      logger.logOperation(
        'Preferences updated successfully',
        context.correlationId,
        SERVICE_CONFIG.name,
        'executeBatchUpdate',
        _metrics!,
        { _userId, updateCategories: Object.keys(updates) }
      );

      // Track telemetry for preference changes
      this.trackPreferenceChanges(_userId, updates, context.correlationId);

      return {
        success: true,
        data: undefined,
        correlationId: context.correlationId,
        _metrics
      };

    } catch (error) {
      const _metrics = logger.endOperation(context.operationId);

      // Invalidate cache on error
      this.cacheRevision++;
      this.cache.invalidate(_userId);

      if (error instanceof ServiceError) {
        throw error;
      }

      throw ErrorFactory.database(
        ErrorCode.INTERNAL_SERVER_ERROR,
        'Unexpected error in batch update execution',
        false,
        context.correlationId,
        { originalError: error }
      );
    }
  }

  /**
   * Merge preferences for optimistic updates
   */
  private mergePreferences(
    current: UserPreferences,
    updates: UpdatePreferencesRequest
  ): UserPreferences {
    return {
      ...current,
      uiPreferences: updates.uiPreferences 
        ? { ...current.uiPreferences, ...updates.uiPreferences }
        : current.uiPreferences,
      workflowPreferences: updates.workflowPreferences
        ? { ...current.workflowPreferences, ...updates.workflowPreferences }
        : current.workflowPreferences,
      notificationPreferences: updates.notificationPreferences
        ? { ...current.notificationPreferences, ...updates.notificationPreferences }
        : current.notificationPreferences,
      integrationPreferences: updates.integrationPreferences
        ? { ...current.integrationPreferences, ...updates.integrationPreferences }
        : current.integrationPreferences,
      filterPreferences: updates.filterPreferences
        ? { ...current.filterPreferences, ...updates.filterPreferences }
        : current.filterPreferences,
      featureSettings: updates.featureSettings
        ? { ...current.featureSettings, ...updates.featureSettings }
        : current.featureSettings,
      audit: {
        ...current.audit,
        updatedAt: new Date().toISOString(),
        updatedBy: current.userId
      }
    };
  }

  /**
   * Map Supabase row to domain model
   */
  private mapSupabaseToPreferences(row: Record<string, unknown>): UserPreferences {
    return {
      id: row.id as string,
      _userId: row.user_id as string,
      organizationId: row.organization_id as string,
      uiPreferences: (row.ui_preferences as UIPreferences | null)
        || PreferencesFactory.getDefaultUIPreferences(),
      workflowPreferences: (row.workflow_preferences as WorkflowPreferences | null)
        || PreferencesFactory.getDefaultWorkflowPreferences(),
      notificationPreferences: (row.notification_preferences as NotificationPreferences | null)
        || PreferencesFactory.getDefaultNotificationPreferences(),
      integrationPreferences: (row.integration_preferences as IntegrationPreferences | null)
        || PreferencesFactory.getDefaultIntegrationPreferences(),
      filterPreferences: (row.filter_preferences as FilterPreferences | null)
        || PreferencesFactory.getDefaultFilterPreferences(),
      featureSettings: (row.feature_settings as FeatureSettings | null)
        || PreferencesFactory.getDefaultFeatureSettings(),
      schemaVersion: row.schema_version as string || String(SERVICE_CONFIG.schemaVersion),
      audit: {
        createdAt: row.created_at as string,
        createdBy: row.user_id as string,
        updatedAt: row.updated_at as string,
        updatedBy: row.user_id as string,
        version: row.version as number || 1
      }
    };
  }

  /**
   * Track preference changes for analytics
   */
  private trackPreferenceChanges(
    _userId: string,
    updates: UpdatePreferencesRequest,
    correlationId: string
  ): void {
    // In production, this would send to analytics service
    logger.info('Preference changes tracked', correlationId, SERVICE_CONFIG.name, 'trackPreferenceChanges', {
      _userId,
      changes: {
        uiPreferences: updates.uiPreferences ? Object.keys(updates.uiPreferences) : [],
        workflowPreferences: updates.workflowPreferences ? Object.keys(updates.workflowPreferences) : [],
        notificationPreferences: updates.notificationPreferences ? Object.keys(updates.notificationPreferences) : [],
        integrationPreferences: updates.integrationPreferences ? Object.keys(updates.integrationPreferences) : [],
        filterPreferences: updates.filterPreferences ? Object.keys(updates.filterPreferences) : [],
        featureSettings: updates.featureSettings ? Object.keys(updates.featureSettings) : []
      }
    });
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return this.cache.getStats();
  }

  /**
   * Get debounce statistics
   */
  getDebounceStats() {
    return {
      pendingUpdates: this.debounceManager.getPendingCount()
    };
  }

  /**
   * Flush all pending updates (for testing/shutdown)
   */
  async flushPendingUpdates(): Promise<void> {
    const preparationsAtFlush = this.preferencePreparationTail;
    await preparationsAtFlush;
    await this.debounceManager.flushAll();
  }

  /**
   * Clear cache (for testing)
   */
  clearCache(): void {
    this.cacheRevision++;
    this.cache.clear();
  }
}

/**
 * Create RPC function for atomic preference updates
 * This SQL function should be added to Supabase
 * 
 * CREATE OR REPLACE FUNCTION update_user_preferences(
 *   p_user_id UUID,
 *   p_updates JSONB
 * ) RETURNS VOID AS $$
 * BEGIN
 *   UPDATE user_preferences
 *   SET 
 *     ui_preferences = COALESCE(p_updates->>'ui_preferences', ui_preferences)::jsonb,
 *     workflow_preferences = COALESCE(p_updates->>'workflow_preferences', workflow_preferences)::jsonb,
 *     notification_preferences = COALESCE(p_updates->>'notification_preferences', notification_preferences)::jsonb,
 *     integration_preferences = COALESCE(p_updates->>'integration_preferences', integration_preferences)::jsonb,
 *     filter_preferences = COALESCE(p_updates->>'filter_preferences', filter_preferences)::jsonb,
 *     feature_settings = COALESCE(p_updates->>'feature_settings', feature_settings)::jsonb,
 *     updated_at = NOW()
 *   WHERE user_id = p_user_id;
 * END;
 * $$ LANGUAGE plpgsql SECURITY DEFINER;
 */

/**
 * Default service instance
 */
export const userPreferencesService = UserPreferencesService.getInstance();

/**
 * Unit test stubs for future implementation
 */
// describe('UserPreferencesService', () => {
//   describe('getPreferences', () => {
//     it('should return existing preferences when available');
//     it('should create default preferences for new users');
//     it('should cache preferences after fetching');
//     it('should use cached preferences when available');
//     it('should handle authentication errors');
//   });
//
//   describe('updateUIPreferences', () => {
//     it('should update UI preferences with debouncing');
//     it('should merge partial updates correctly');
//     it('should apply optimistic updates to cache');
//     it('should rollback on failure');
//   });
//
//   describe('batchUpdate', () => {
//     it('should batch multiple updates within debounce window');
//     it('should validate all update requests');
//     it('should handle concurrent updates gracefully');
//   });
//
//   describe('resetToDefaults', () => {
//     it('should reset all preferences to role-based defaults');
//     it('should invalidate cache after reset');
//   });
//
//   describe('debouncing', () => {
//     it('should debounce rapid updates');
//     it('should merge updates within debounce window');
//     it('should flush pending updates on shutdown');
//   });
// });
